import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  TextField,
  Grid,
  Avatar,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
} from '@mui/material';
import {
  ArrowBack,
  PhotoCamera,
  Save,
  Cancel,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import apiService from '../../services/apiService';
import { toast } from 'react-toastify';
import { validateCustomerForm, getValidationError, CustomerFormData } from '../../utils/validation';

const EditCustomer: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [loading, setLoading] = useState(true);
  const [subscriptions, setSubscriptions] = useState<any[]>([]);
  const [errors, setErrors] = useState<any>({});
  const [formData, setFormData] = useState({
    fullName: '',
    customerType: '',
    email: '',
    phone: '',
    companyName: '',
    contactPerson: '',
    streetArea: '',
    city: '',
    state: '',
    country: '',
    postalCode: '',
    creditLimit: '',
    currentBalance: '',
    notes: '',
    deliveryStartDate: '',
    deliverySchedule: '',
    subscriptionType: '',
    package: '',
    quantity: '',
    startDate: '',
    endDate: '',
  });

  useEffect(() => {
    const fetchData = async () => {
      if (!id) return;
      
      try {
        setLoading(true);
        
        // Fetch subscriptions list
        const subsResponse = await apiService.getSubscriptions();
        setSubscriptions(subsResponse.data || []);
        
        // Fetch customer data
        const response = await apiService.getCustomer(parseInt(id));
        const customer = response.data;
        
        // Get active subscription data if available
        const activeSubscription = customer.subscriptions?.find((sub: any) => sub.status === 'active') || customer.subscriptions?.[0];
        
        setFormData({
          fullName: customer.name || '',
          customerType: customer.status || '',
          email: customer.email || '',
          phone: customer.phone || '',
          companyName: customer.company_name || '',
          contactPerson: customer.contact_person || '',
          streetArea: customer.address || '',
          city: customer.city || '',
          state: customer.state || '',
          country: customer.country || '',
          postalCode: customer.postal_code || '',
          creditLimit: customer.credit_limit?.toString() || '',
          currentBalance: customer.current_balance?.toString() || '',
          notes: customer.notes || '',
          deliveryStartDate: customer.created_at?.split('T')[0] || '',
          deliverySchedule: activeSubscription?.frequency?.name || '',
          subscriptionType: activeSubscription?.subscription_type?.name || '',
          package: activeSubscription?.id?.toString() || '',
          quantity: '',
          startDate: activeSubscription?.start_date?.split('T')[0] || '',
          endDate: activeSubscription?.end_date?.split('T')[0] || '',
        });
      } catch (error: any) {
        console.error('Failed to fetch customer:', error);
        toast.error('Failed to load customer data');
        navigate('/customers');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id, navigate]);

  const handleInputChange = (field: string) => (event: any) => {
    setFormData({
      ...formData,
      [field]: event.target.value,
    });
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors({
        ...errors,
        [field]: ''
      });
    }
  };

  const validateForm = (): boolean => {
    const validationErrors = validateCustomerForm(formData as CustomerFormData);
    
    if (validationErrors) {
      setErrors(validationErrors);
      return false;
    }
    
    setErrors({});
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!id) return;
    
    // Validate form using validate.js
    const validationErrors = validateCustomerForm(formData as CustomerFormData);
    
    if (validationErrors) {
      setErrors(validationErrors);
      toast.error('Please fix the validation errors');
      return;
    }
    
    setErrors({});
    
    try {
      // Update customer data
      const response = await apiService.updateCustomer(parseInt(id), {
        name: formData.fullName,
        email: formData.email,
        phone: formData.phone,
        status: formData.customerType || 'active',
        company_name: formData.companyName,
        contact_person: formData.contactPerson,
        address: formData.streetArea,
        city: formData.city,
        state: formData.state,
        country: formData.country,
        postal_code: formData.postalCode,
        credit_limit: formData.creditLimit ? parseFloat(formData.creditLimit) : null,
        current_balance: formData.currentBalance ? parseFloat(formData.currentBalance) : null,
        notes: formData.notes,
      });
      
      console.log('Update response:', response);
      console.log('Response success:', response.success);
      console.log('Response message:', response.message);
      
      // If a subscription is selected, assign it to the customer
      if (formData.package) {
        const subscriptionId = parseInt(formData.package);
        const selectedSubscription = subscriptions.find(sub => sub.id === subscriptionId);
        
        if (selectedSubscription) {
          await apiService.assignSubscriptionsToCustomer(parseInt(id), [
            {
              subscription_id: subscriptionId,
              status: 'active',
              start_date: new Date().toISOString().split('T')[0],
            }
          ]);
        }
      }
      
      console.log('About to check response.success:', response.success);
      if (response.success) {
        console.log('Showing success message');
        toast.success('Customer updated successfully');
        // Add a small delay to ensure toast is visible before navigation
        setTimeout(() => {
          navigate('/customers');
        }, 1000);
      } else {
        console.log('Showing error message:', response.message);
        toast.error(response.message || 'Failed to update customer');
      }
    } catch (error: any) {
      console.error('Failed to update customer:', error);
      toast.error(error.response?.data?.message || error.message || 'Failed to update customer');
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <IconButton onClick={() => navigate(-1)} sx={{ mr: 2 }}>
          <ArrowBack />
        </IconButton>
        <Typography variant="h4" sx={{ fontWeight: 600 }}>
          Edit Customer
        </Typography>
      </Box>

      <Card>
        <CardContent>
          <Box component="form" onSubmit={handleSubmit}>
            <Grid container spacing={4}>
              <Grid size={{ xs: 12, md: 4 }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <Avatar
                    sx={{
                      width: 120,
                      height: 120,
                      mb: 2,
                      backgroundColor: 'primary.main',
                    }}
                  >
                    {formData.fullName.charAt(0)}
                  </Avatar>
                  <Button
                    variant="contained"
                    startIcon={<PhotoCamera />}
                    sx={{ mb: 2 }}
                  >
                    Change
                  </Button>
                  <Typography variant="body2" color="text.secondary" align="center">
                    Allowed format JPG, JPEG, and PNG
                  </Typography>
                  <Typography variant="body2" color="text.secondary" align="center">
                    Max file size 2MB
                  </Typography>
                </Box>
              </Grid>

              <Grid size={{ xs: 12, md: 8 }}>
                <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
                  Contact Information
                </Typography>
                <Grid container spacing={2} sx={{ mb: 3 }}>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      fullWidth
                      label="Full Name"
                      placeholder="Enter"
                      value={formData.fullName}
                      onChange={handleInputChange('fullName')}
                      error={!!errors.fullName}
                      helperText={errors.fullName}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <FormControl fullWidth required>
                      <InputLabel>Status</InputLabel>
                      <Select
                        value={formData.customerType}
                        label="Status"
                        onChange={handleInputChange('customerType')}
                      >
                        <MenuItem value="active">Active</MenuItem>
                        <MenuItem value="inactive">Inactive</MenuItem>
                        <MenuItem value="suspended">Suspended</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      fullWidth
                      label="Email Address"
                      placeholder="Enter"
                      type="email"
                      value={formData.email}
                      onChange={handleInputChange('email')}
                      error={!!errors.email}
                      helperText={errors.email}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      fullWidth
                      label="Phone Number"
                      placeholder="Enter"
                      value={formData.phone}
                      onChange={handleInputChange('phone')}
                      error={!!errors.phone}
                      helperText={errors.phone}
                    />
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <TextField
                      fullWidth
                      label="Notes"
                      multiline
                      rows={3}
                      placeholder="Enter"
                      value={formData.notes}
                      onChange={handleInputChange('notes')}
                    />
                  </Grid>
                </Grid>

                <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', mt: 3 }}>
                  Company Information
                </Typography>
                <Grid container spacing={2} sx={{ mb: 3 }}>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      fullWidth
                      label="Company Name"
                      placeholder="Enter"
                      value={formData.companyName}
                      onChange={handleInputChange('companyName')}
                      error={!!errors.companyName}
                      helperText={errors.companyName}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      fullWidth
                      label="Contact Person"
                      placeholder="Enter"
                      value={formData.contactPerson}
                      onChange={handleInputChange('contactPerson')}
                      error={!!errors.contactPerson}
                      helperText={errors.contactPerson}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      fullWidth
                      label="Credit Limit"
                      type="number"
                      placeholder="Enter"
                      value={formData.creditLimit}
                      onChange={handleInputChange('creditLimit')}
                      error={!!errors.creditLimit}
                      helperText={errors.creditLimit}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      fullWidth
                      label="Current Balance"
                      type="number"
                      placeholder="Enter"
                      value={formData.currentBalance}
                      onChange={handleInputChange('currentBalance')}
                      error={!!errors.currentBalance}
                      helperText={errors.currentBalance}
                    />
                  </Grid>
                </Grid>

                <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
                  Address & Delivery
                </Typography>
                <Grid container spacing={2} sx={{ mb: 3 }}>
                  <Grid size={{ xs: 12 }}>
                    <TextField
                      fullWidth
                      label="Street Area"
                      placeholder="Enter"
                      value={formData.streetArea}
                      onChange={handleInputChange('streetArea')}
                      error={!!errors.streetArea}
                      helperText={errors.streetArea}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <FormControl fullWidth>
                      <InputLabel>City</InputLabel>
                      <Select
                        value={formData.city}
                        label="City"
                        onChange={handleInputChange('city')}
                      >
                        <MenuItem value="Lorem ipsum">Lorem ipsum</MenuItem>
                        <MenuItem value="Dubai">Dubai</MenuItem>
                        <MenuItem value="Abu Dhabi">Abu Dhabi</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      fullWidth
                      label="State"
                      placeholder="Enter"
                      value={formData.state}
                      onChange={handleInputChange('state')}
                      error={!!errors.state}
                      helperText={errors.state}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      fullWidth
                      label="Country"
                      placeholder="Enter"
                      value={formData.country}
                      onChange={handleInputChange('country')}
                      error={!!errors.country}
                      helperText={errors.country}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      fullWidth
                      label="Postal Code"
                      placeholder="Enter"
                      value={formData.postalCode}
                      onChange={handleInputChange('postalCode')}
                      error={!!errors.postalCode}
                      helperText={errors.postalCode}
                    />
                  </Grid>
                </Grid>

                <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
                  Subscription
                </Typography>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12 }}>
                    <FormControl fullWidth>
                      <InputLabel>Select Subscription</InputLabel>
                      <Select
                        value={formData.package}
                        label="Select Subscription"
                        onChange={handleInputChange('package')}
                      >
                        <MenuItem value="">None</MenuItem>
                        {subscriptions.map((sub) => (
                          <MenuItem key={sub.id} value={sub.id.toString()}>
                            {sub.name} - {sub.subscription_number}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 4 }}>
              <Button
                variant="outlined"
                startIcon={<Cancel />}
                onClick={() => navigate('/customers')}
                sx={{ px: 4 }}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                startIcon={<Save />}
                sx={{ px: 4 }}
              >
                Edit Customer
              </Button>
            </Box>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default EditCustomer;

